import React, {
	FormEvent,
	ReactNode,
	useEffect,
	useRef,
	useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Modal from '@/components/Modal/Modal';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import type { ResetPassword } from '../exceptions/resetPasswordError';
import { resetPasswordRequest } from '../userActions';
import './ResetPasswordModal.scss';
import iconshide from '@/assets/images/icons-hide.svg';
import iconsshow from '@/assets/images/icons-show.svg';

type Props = {
	show: boolean;
	onClose: () => void;
};

export default function ResetPasswordModal({
	show,
	onClose
}: Props): JSX.Element {
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		setIsVisible(show);
	}, [show]);

	const [isLoading, isResetPasswordSucceed, resetPasswordFailure, user] =
		useSelector<RootState, [boolean, boolean, ResetPassword]>(
			(state: RootState) => [
				state.user.getIn(['resetPassword', 'isLoading']),
				state.user.getIn(['resetPassword', 'success']),
				state.user.getIn(['resetPassword', 'failure']).toJS(),
				state.user.getIn(['authorize', 'user']).toJS()
			],
			shallowEqual
		);

	const currentPasswordRef = useRef<HTMLInputElement>(null);
	const newPasswordRef = useRef<HTMLInputElement>(null);
	const retryNewPasswordRef = useRef<HTMLInputElement>(null);
	const dispatch = useDispatch();

	const resetPassword = (
		currentPassword: string,
		newPassword: string,
		retryNewPassword: string
	) =>
		dispatch(
			resetPasswordRequest({
				currentPassword,
				newPassword,
				retryNewPassword
			})
		);

	const submit = (e: FormEvent) => {
		e.preventDefault();

		resetPassword(
			currentPasswordRef.current
				? String(currentPasswordRef.current.value).trim()
				: '',
			newPasswordRef.current
				? String(newPasswordRef.current.value).trim()
				: '',
			retryNewPasswordRef.current
				? String(retryNewPasswordRef.current.value).trim()
				: ''
		);
	};

	const [currPasswordShown, setCurrPasswordShown] = useState(false);
	const [newPasswordShown, setNewPasswordShown] = useState(false);
	const [repeatPasswordShown, setRepeatPasswordShown] = useState(false);

	const togglePassword = el => {
		if (el.type === 'password') {
			el.type = 'text';
		} else {
			el.type = 'password';
		}
		switch (el.id) {
			case 'currPassword':
				setCurrPasswordShown(!currPasswordShown);
				break;
			case 'newPassword':
				setNewPasswordShown(!newPasswordShown);
				break;
			case 'newPasswordConfirm':
				setRepeatPasswordShown(!repeatPasswordShown);
				break;
		}
	};

	useEffect(() => {
		if (isResetPasswordSucceed) {
			onClose();
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User submitted password change request',
					properties: {
						successful: 'Yes'
					}
				});
			}
		}
	}, [isResetPasswordSucceed]);

	return (
		<Modal
			bodyClass="reset-password-holder"
			onClose={onClose}
			title="Reset password"
			overlayClose
			content={
				<form onSubmit={submit} className="w-full pt-4">
					<div className="mb-1">
						<p className="font-semibold">Current password</p>
						<input
							type="password"
							id="currPassword"
							ref={el => {
								el?.focus();
								(currentPasswordRef.current as ReactNode) = el;
							}}
							className={`password listModal-input mt-2 w-full reset-field${
								resetPasswordFailure.currentPassword
									? 'reset-field--invalid'
									: ''
							}`}
						/>
						{currPasswordShown ? (
							<img
								src={iconsshow}
								className="password-icon"
								onClick={() => {
									togglePassword(currentPasswordRef.current);
								}}
							/>
						) : (
							<img
								src={iconshide}
								className="password-icon"
								onClick={() => {
									togglePassword(currentPasswordRef.current);
								}}
							/>
						)}
						<p className="errors pt-2 text-sm">
							{resetPasswordFailure.currentPassword ?? ''}
						</p>
					</div>
					<div className="mb-1">
						<p className="font-semibold">New password</p>
						<input
							type="password"
							id="newPassword"
							ref={newPasswordRef}
							className={`password listModal-input mt-2 w-full reset-field${
								resetPasswordFailure.newPassword
									? 'reset-field--invalid'
									: ''
							}`}
						/>
						{newPasswordShown ? (
							<img
								src={iconsshow}
								className="password-icon"
								onClick={() => {
									togglePassword(newPasswordRef.current);
								}}
							/>
						) : (
							<img
								src={iconshide}
								className="password-icon"
								onClick={() => {
									togglePassword(newPasswordRef.current);
								}}
							/>
						)}
						<p className="errors pt-2 text-sm">
							{resetPasswordFailure.newPassword ?? ''}
						</p>
					</div>
					<div className="mb-1">
						<p className="font-semibold">Repeat new password</p>
						<input
							type="password"
							id="newPasswordConfirm"
							ref={retryNewPasswordRef}
							className={`password listModal-input mt-2 w-full reset-field${
								resetPasswordFailure.retryNewPassword
									? 'reset-field--invalid'
									: ''
							}`}
						/>
						{repeatPasswordShown ? (
							<img
								src={iconsshow}
								className="password-icon"
								onClick={() => {
									togglePassword(retryNewPasswordRef.current);
								}}
							/>
						) : (
							<img
								src={iconshide}
								className="password-icon"
								onClick={() => {
									togglePassword(retryNewPasswordRef.current);
								}}
							/>
						)}
						<p className="errors pt-2 text-sm">
							{resetPasswordFailure.retryNewPassword ?? ''}
						</p>
					</div>
					<div className="flex justify-end">
						<button
							type="submit"
							onClick={submit}
							className={`primary px-4 py-2 rounded${
								isLoading ? 'cursor-default opacity-60' : ''
							}`}
						>
							Save
						</button>
					</div>
				</form>
			}
			buttons={[]}
			show={isVisible}
		/>
	);
}
